import {
	Button,
	Loader,
	Menu,
	Modal,
	Popup,
	Table,
} from '@mairistem/composants';
import {
	formatMontant,
	type Gabarit,
	getErrorMessage,
} from '@mairistem/finances-utils';
import type { DropdownOption } from '@mairistem/liste';
import { isNewIdentifiant, newIdentifiant, request } from '@mairistem/tools';
import dayjs from 'dayjs';
import { isNil } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import AssistantVersements, {
	STEP_CARACTERISTIQUE,
} from '@/src/components/SubventionFichePanel/ConditionsVersements/AssistantVersement/AssistantVersements';
import PreparationModal from '@/src/components/SubventionFichePanel/ConditionsVersements/PreparationModal';
import ConditionVersementEtat from '@/src/constants/ConditionVersementEtat';
import type { ConditionVersement } from '@/src/interfaces/conditionVersement';
import type { PieceComptabilite } from '@/src/interfaces/pieceComptabilite';
import type { Subvention } from '@/src/interfaces/subvention';
import renderEtatIcons from '@/src/utils/etatsIcons';
import { toDateString } from '@/src/utils/tools';
import { URL_PARAMETRES_ENTITES } from '@/src/utils/url';
import '@/src/components/SubventionFichePanel/ConditionsVersements/ConditionsVersements.css';

const LIBELLE_TYPE_PIECE = {
	M: 'mouvement',
	E: 'engagement',
};

interface ConditionsVersementsProps {
	conditionsVersements: ConditionVersement[];
	deletedConditionsVersements: number[];
	gabaritsPj: Gabarit[];
	identifiant: number;
	identifiantEntite: number;
	montantSubvention: number;
	onChange: (data: Partial<Subvention>, isMerge?: boolean) => void;
	subvention: Subvention;
}

interface PieceDemande {
	identifiantExercice: number;
	identifiantPiece: number;
	typePiece: string;
}

const ConditionsVersements = ({
	conditionsVersements = [],
	deletedConditionsVersements = [],
	gabaritsPj = null,
	identifiant = null,
	identifiantEntite = null,
	montantSubvention = 0,
	onChange = null,
	subvention = null,
}: ConditionsVersementsProps) => {
	const [openModal, setOpenModal] = React.useState(false);
	const [openConditionMenuId, setOpenConditionMenuId] = React.useState<
		number | string
	>(null);
	const [currentCondition, setCurrentCondition] = React.useState(null);
	const [assistantStep, setAssistantStep] =
		React.useState(STEP_CARACTERISTIQUE);
	const [budgetOptions, setBudgetOptions] = React.useState<DropdownOption[]>(
		[],
	);
	// information de la piece cree dans le but de l ouvrir dans la compta
	const [pieceDemande, setPieceDemande] = React.useState<Partial<PieceDemande>>(
		{},
	);
	const [isCreatingPiece, setIsCreatingPiece] =
		React.useState<Partial<boolean>>(false);
	const [openPreparationDemande, setOpenPreparationDemande] =
		React.useState(false);
	const [loadingOpenComptabilite, setLoadingOpenComptabilite] =
		React.useState(false);
	const preparationModalRef = React.useRef(
		document.getElementById('root')?.firstChild?.lastChild?.firstChild,
	);

	React.useEffect(() => {
		if (!isNil(identifiantEntite)) {
			// TODO: Typage de l'entite
			request
				.get<unknown, any>(
					`${URL_PARAMETRES_ENTITES}?identifiantEntiteSubvention=${identifiantEntite}&withEntite=true`,
				)
				.then(({ data }) => {
					setBudgetOptions(
						data.map((budget) => ({
							key: budget.identifiantEntite,
							text: `${budget?.entite?.libelle}`,
							value: budget.identifiantEntite,
							'data-testid': `budgetOption-${budget.identifiantEntite}`,
						})),
					);
				});
		}
	}, [identifiantEntite]);

	const getExecutionComptable = (mouvements, engagements) => {
		if (mouvements?.length > 0) {
			return 'Titré';
		}

		if (engagements?.length > 0) {
			return 'Engagé';
		}

		return '';
	};

	const getRegle = (condition) => {
		switch (condition.pourcentageExecution) {
			case 0:
				return 'au lancement du projet';
			case 100:
				return 'à la fin de réalisation du projet';
			default:
				return `dès ${condition.pourcentageExecution}% de réalisation du projet`;
		}
	};

	const handleFinish = (versement: ConditionVersement) => {
		let isCreation = true;

		const newConditionsVersements = conditionsVersements.map((condition) => {
			// Si on modifie une condition existante
			if (condition.identifiant === versement.identifiant) {
				isCreation = false;
				return versement;
			}

			return condition;
		});

		if (isCreation) {
			newConditionsVersements.push(versement);
		}

		onChange({
			identifiant,
			conditionsVersements: newConditionsVersements,
		});

		setOpenModal(false);
	};

	const showPreparationDemande = (condition) => {
		setOpenConditionMenuId(null);
		setOpenPreparationDemande(true);
		setCurrentCondition(condition);
	};

	const handleEnvoieDemande = (condition) => {
		setOpenConditionMenuId(null);
		if (
			isNil(condition?.imputationsVersements) ||
			condition?.imputationsVersements?.length === 0
		) {
			toast.error('Veuillez renseigner une imputation pour ce versement');

			return;
		}
		setIsCreatingPiece(true);

		request
			.post(
				`/api/finances/subventionsPercues/conditionVersement/${condition?.identifiant}/envoie`,
			)
			.then((data) => {
				setPieceDemande(data);
			})
			.catch((e) => {
				toast.error(getErrorMessage(e, "Impossible d'effectuer la demande"));
			})
			.finally(() => {
				setIsCreatingPiece(false);
			});
	};

	const handleCloseModal = () => {
		setOpenModal(false);
	};

	const getNewCondition = () => {
		const restant = conditionsVersements.reduce(
			(previous, current) => ({ montant: previous.montant + current.montant }),
			{ montant: 0 },
		);

		const pourcentageSubvention =
			((montantSubvention - restant.montant) * 100) / montantSubvention;
		const {
			identifiantEntiteCompta,
			identifiantSubvention,
			typeBudget,
			annee,
		} = conditionsVersements[conditionsVersements?.length ?? 0 - 1] ?? {};

		return {
			identifiant: newIdentifiant(),
			annee: annee ?? new Date().getFullYear(),
			pourcentageCumuleSubvention: 100,
			montant: montantSubvention - restant.montant,
			pourcentageSubvention,
			identifiantEntite,
			identifiantEntiteCompta,
			identifiantSubvention,
			typeBudget,
			pourcentageExecution: 0,
			deletedNaturesConditions: [],
			naturesConditions: [],
		};
	};

	const openAssistantVersement = (currentCond = null) => {
		setOpenConditionMenuId(null);
		if (currentCond === null) {
			const totalConditions = conditionsVersements.reduce(
				(previous, current) => ({
					montant: previous.montant + current.montant,
				}),
				{ montant: 0 },
			);

			if (totalConditions.montant >= montantSubvention) {
				toast.error(
					"Création d'un versement impossible, le montant de la subvention est déjà entièrement réparti sur les versements existants",
				);

				return;
			}
		}
		setAssistantStep(STEP_CARACTERISTIQUE);
		setCurrentCondition(currentCond ?? getNewCondition());
		setOpenModal(true);
	};

	const handleDeleteCondition = (identifiantConditionVersement) => {
		setOpenConditionMenuId(null);
		if (
			!isNil(identifiantConditionVersement) &&
			!isNewIdentifiant(identifiantConditionVersement)
		) {
			onChange({
				identifiant,
				deletedConditionsVersements: [
					...deletedConditionsVersements,
					identifiantConditionVersement,
				],
			});
		}

		onChange({
			identifiant,
			conditionsVersements: conditionsVersements.filter(
				(c) => c.identifiant !== identifiantConditionVersement,
			),
		});
	};

	const getMontantPrecedentesConditions = React.useCallback(() => {
		let montant = 0;

		conditionsVersements.some((condition) => {
			if (condition.identifiant === currentCondition.identifiant) {
				return true;
			}

			montant += condition.montant;

			return false;
		});

		return montant;
	}, [conditionsVersements, currentCondition]);

	const handleLibelleDatalist = (): string[] => {
		let acompteCount = 0;
		let indexCondition = null;

		conditionsVersements.some((condition, i) => {
			if (condition.identifiant === currentCondition.identifiant) {
				indexCondition = i;
				return true;
			}

			if (condition.libelle.toUpperCase().includes('ACOMPTE')) {
				acompteCount += 1;
			}

			return false;
		});
		indexCondition = indexCondition ?? conditionsVersements.length;

		if (indexCondition === 0) {
			return ['Avance', '1er acompte', 'Solde', 'Versement unique'];
		}

		if (acompteCount > 0) {
			return [`${acompteCount + 1}ème acompte`, 'Solde'];
		}

		return ['1er acompte', 'Solde'];
	};

	const getBugetLibelle = (identifiantEntiteCompta) => {
		const budget = budgetOptions.find(
			(b) => b.value === identifiantEntiteCompta,
		);
		return budget?.text;
	};

	const getLibelleImputation = (imputation) => {
		if (isNil(imputation) || isNil(imputation?.libelle)) {
			return '';
		}

		return `R ${imputation?.section} ${imputation?.codeChapitre} ${imputation?.libelle}`;
	};

	const hanldeCloseModalComptabilite = () => {
		setPieceDemande({});
	};

	const handleOpenComptabilite = () => {
		setPieceDemande({});
		const params = new URLSearchParams();
		params.append(
			'identifiantExercice',
			pieceDemande?.identifiantExercice?.toString(),
		);
		params.append(
			'identifiantPiece',
			pieceDemande?.identifiantPiece?.toString(),
		);
		params.append('typePiece', pieceDemande?.typePiece);
		params.append('idClient', sessionStorage.getItem('idClient'));
		params.append('component', sessionStorage.getItem('component'));

		setLoadingOpenComptabilite(true);
		request
			.get<unknown, PieceComptabilite>(
				`/api/finances/subventionsPercues/conditionVersement/open?${params.toString()}`,
			)
			.then((data) => {
				if (data?.url) {
					window.open(data?.url, '_blank');
				} else {
					toast.error("Erreur lors de la visualisation de l'écriture.");
				}
			})
			.catch((e) => {
				toast.error(getErrorMessage(e, "Impossible d'ouvrir la pièce"));
			})
			.finally(() => {
				setLoadingOpenComptabilite(false);
			});
	};

	// On met à jour l'état et la date de demande de la condition
	const handleDownloadSuccess = (condition: ConditionVersement) => {
		onChange(
			{
				identifiant,
				conditionsVersements: conditionsVersements.map((v) => {
					if (v.identifiant === condition.identifiant) {
						return {
							...v,
							etat: ConditionVersementEtat.VERSEMENT_DEMANDE,
							dateDemande: dayjs().format('YYYY-MM-DD'),
						};
					}

					return v;
				}),
			},
			true,
		);
	};

	return (
		<>
			<Table
				celled
				striped
				id="versementsTable"
				data-testid="conditionVersementTable"
			>
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell>Versement</Table.HeaderCell>
						<Table.HeaderCell>Montant</Table.HeaderCell>
						<Table.HeaderCell>Règles</Table.HeaderCell>
						<Table.HeaderCell>Inscription budgétaire</Table.HeaderCell>
						<Table.HeaderCell>Date de demande</Table.HeaderCell>
						<Table.HeaderCell>Exécution comptable</Table.HeaderCell>
						<Table.HeaderCell className="menu">Action</Table.HeaderCell>
					</Table.Row>
				</Table.Header>

				<Table.Body>
					{conditionsVersements?.map((condition, i) => (
						<Table.Row
							key={condition.identifiant}
							onDoubleClick={() => {
								openAssistantVersement(condition);
							}}
							data-testid={`condition-${i}`}
						>
							<Table.Cell data-testid="libelle">{condition.libelle}</Table.Cell>
							<Table.Cell textAlign="right">
								{formatMontant(condition.montant)}
							</Table.Cell>
							<Table.Cell>
								<div className="reglesTableau">
									<div>
										<div>{getRegle(condition)}</div>
										<div>
											{condition?.dateButoire &&
												`Date butoire: ${toDateString(condition.dateButoire)}`}
										</div>
									</div>
									<div className="etatsIconsTableau">
										{renderEtatIcons(condition)}
									</div>
								</div>
							</Table.Cell>
							<Table.Cell>
								<div>{getBugetLibelle(condition?.identifiantEntiteCompta)}</div>
								<div>
									Exercice {condition.annee}{' '}
									{condition.typeBudget && `(${condition.typeBudget})`}
								</div>
								<div>
									{getLibelleImputation(condition?.imputationsVersements?.[0])}
								</div>
							</Table.Cell>
							<Table.Cell>{toDateString(condition.dateDemande)}</Table.Cell>
							<Table.Cell>
								{getExecutionComptable(
									condition.mouvements,
									condition?.engagements,
								)}
							</Table.Cell>

							<Table.Cell collapsing className="menu">
								{' '}
								<Popup
									trigger={
										<Button
											icon="bars"
											inverted
											compact
											onClick={() =>
												setOpenConditionMenuId(condition?.identifiant)
											}
											data-testid={`ouvrirMenuCondition-${condition?.identifiant}`}
										/>
									}
									as={Menu.Item}
									content={
										<Menu fluid vertical secondary>
											<Menu.Item
												name="table_update"
												onClick={() => openAssistantVersement(condition)}
												data-testid={`modifierCondition-${condition.identifiant}`}
											>
												Modifier
											</Menu.Item>
											<Menu.Item
												name="table_remove"
												onClick={() =>
													handleDeleteCondition(condition?.identifiant)
												}
												data-testid={`supprimerCondition-${condition.identifiant}`}
											>
												Supprimer
											</Menu.Item>

											{(condition.etat ===
												ConditionVersementEtat.VERSEMENT_A_DEMANDER ||
												condition.etat ===
													ConditionVersementEtat.VERSEMENT_DEMANDE) && (
												<>
													<Menu.Item
														name="table_envoie"
														onClick={() => showPreparationDemande(condition)}
														data-testid={`preparerDemande-${condition.identifiant}`}
													>
														Préparer la demande
													</Menu.Item>

													<Menu.Item
														name="table_envoie"
														onClick={() => handleEnvoieDemande(condition)}
														data-testid={`envoyerDemande-${condition.identifiant}`}
													>
														{condition?.doitEngager
															? "Créer l'engagement"
															: 'Créer le titre'}
													</Menu.Item>
												</>
											)}
										</Menu>
									}
									on="click"
									open={openConditionMenuId === condition?.identifiant}
									onClose={() => setOpenConditionMenuId(null)}
									onOpen={() => setOpenConditionMenuId(condition?.identifiant)}
									position="bottom right"
									popperModifiers={{
										// @ts-expect-error
										preventOverflow: {
											boundariesElement: 'offsetParent',
										},
									}}
								/>
							</Table.Cell>
						</Table.Row>
					))}
				</Table.Body>

				<Table.Footer>
					<Table.Row key="new" textAlign="center">
						<Table.HeaderCell
							onClick={() => openAssistantVersement()}
							colSpan="100%"
						>
							<Button
								circular
								icon="add"
								compact
								basic
								size="small"
								data-testid="addConditionVersementButton"
							/>
						</Table.HeaderCell>
					</Table.Row>
				</Table.Footer>
			</Table>

			{openModal && (
				<AssistantVersements
					budgetOptions={budgetOptions}
					conditionVersement={currentCondition}
					gabaritsPj={gabaritsPj}
					montantPrecedentesConditions={getMontantPrecedentesConditions()}
					montantSubvention={montantSubvention}
					onClose={handleCloseModal}
					onFinish={handleFinish}
					onLibelleDatalist={handleLibelleDatalist}
					open={openModal}
					setStep={setAssistantStep}
					step={assistantStep}
				/>
			)}
			{(!isNil(pieceDemande?.identifiantPiece) || isCreatingPiece) && (
				<Modal closeIcon open>
					<Modal.Header key="header">Versement</Modal.Header>

					<Modal.Content key="content" className="modalCreation">
						{isCreatingPiece ? (
							<Loader inverted inline="centered">
								Création de la pièce
							</Loader>
						) : (
							<>
								Un {LIBELLE_TYPE_PIECE[pieceDemande?.typePiece] ?? ''} a été
								créé. Souhaitez vous le visualiser ?
							</>
						)}
					</Modal.Content>

					<Modal.Actions>
						<Button
							primary
							disabled={isCreatingPiece}
							onClick={handleOpenComptabilite}
							loading={loadingOpenComptabilite}
							data-testid="visuMouvBoutonOui"
						>
							Oui
						</Button>
						<Button
							disabled={isCreatingPiece}
							onClick={hanldeCloseModalComptabilite}
							data-testid="visuMouvBoutonNon"
						>
							Non
						</Button>
					</Modal.Actions>
				</Modal>
			)}

			{openPreparationDemande && (
				<PreparationModal
					conditionVersement={currentCondition}
					subvention={subvention}
					onClose={() => setOpenPreparationDemande(false)}
					onDownloadSuccess={handleDownloadSuccess}
					mountNode={preparationModalRef?.current}
				/>
			)}
		</>
	);
};

export default ConditionsVersements;
